import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Store, FileUp, CalendarClock, ListChecks, ReceiptText, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';

/**
 * /nursery/retailers — one place for the work a large retailer (Bunnings
 * first) brings: its purchase orders, its cross dock pick days and the range
 * file its stores order from. They were three cards on the nursery dashboard;
 * here each says what it is for. Remittances are the Accounts module's and
 * show only to someone with an accounts seat.
 */

// Label and detail of each are `tools.<key>.label` / `tools.<key>.detail`.
const TOOLS = [
  { key: 'import', icon: FileUp, path: '/nursery/orders/import' },
  { key: 'crossDock', icon: CalendarClock, path: '/nursery/cross-dock' },
  { key: 'range', icon: ListChecks, path: '/nursery/retailer-range' },
];

const REMITTANCES = { key: 'remittances', icon: ReceiptText, path: '/accounts/remittances' };

function RetailerHub() {
  const { t } = useTranslation('nursery', { keyPrefix: 'RetailerHub' });
  const { hasAccess: canManage, loading } = useModuleLicense('nursery', ['manager', 'admin']);
  const { hasAccess: hasAccounts } = useModuleLicense('accounts', ['user', 'admin']);

  if (loading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what={t('seatWhat')} />;

  const tools = hasAccounts ? [...TOOLS, REMITTANCES] : TOOLS;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Store className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <Link key={tool.path} to={tool.path}
              className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all">
              <Icon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-800">{t(`tools.${tool.key}.label`)}</span>
                <span className="block text-sm text-neutral-500 mt-0.5">{t(`tools.${tool.key}.detail`)}</span>
              </span>
              <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0 self-center" />
            </Link>
          );
        })}
      </div>
    </>
  );
}

export default RetailerHub;
